import React, { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl } from 'aws-amplify/storage';
import { listCourses } from '../graphql/queries';
import { createCourse, updateCourse, deleteCourse, createLesson } from '../graphql/mutations';
import { 
    FiMonitor, FiVideo, FiEdit, FiPlus, FiArrowLeft, 
    FiImage, FiUsers, FiFileText, FiDollarSign, FiPaperclip, FiTrash2 
} from 'react-icons/fi';

const CourseManager = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('list'); 
    const [editingId, setEditingId] = useState(null);
    const [activeTab, setActiveTab] = useState('general'); 
    const [uploadProgress, setUploadProgress] = useState(0);
    const [attachmentProgress, setAttachmentProgress] = useState(0); 

    const initialForm = {
        title: '',
        subtitle: '',
        instructor: 'Dr. Giovanni Scarvaci',
        priceUSD: '',
        description: '',
        category: 'CURSO',
        targetAudience: '',
        learningObjectives: '',
        duration: '',
        thumbnailUrl: '',
        promoVideoUrl: '',
        status: 'ACTIVO'
    };

    const [form, setForm] = useState(initialForm);
    const [newLesson, setNewLesson] = useState({ 
        title: '', videoUrl: '', order: 1, description: '', content: '' 
    });

    // --- FETCH: ÚNICO LUGAR DONDE AJUSTAMOS LA IMAGEN (PARA QUE SE VEA COMO LOS LIBROS) ---
    const fetchCourses = useCallback(async () => {
        const client = generateClient();
        setLoading(true);
        try {
            const res = await client.graphql({ query: listCourses, authMode: 'apiKey' });
            const items = res.data.listCourses.items;
            const withUrls = await Promise.all(items.map(async (c) => {
                if (c.thumbnailUrl) {
                    try {
                        // AJUSTE SEGURO: Usamos la lógica de tu Inventario de Libros
                        const cleanPath = c.thumbnailUrl.replace(/^public\//, '');
                        const urlRes = await getUrl({ 
                            path: `public/${cleanPath}`,
                            options: { validateObjectExistence: true } 
                        });
                        return { ...c, tempUrl: urlRes.url.toString() };
                    } catch (e) { return c; }
                }
                return c;
            }));
            setCourses(withUrls);
        } catch (e) { console.error(e); } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchCourses(); }, [fetchCourses]);

    const handleDeleteCourse = async (id) => {
        if (!window.confirm("¿ESTÁS SEGURO? Se borrará permanentemente.")) return;
        const client = generateClient();
        setLoading(true);
        try {
            await client.graphql({
                query: deleteCourse,
                variables: { input: { id } },
                authMode: 'userPool'
            });
            alert("✅ CURSO ELIMINADO");
            fetchCourses();
        } catch (e) { alert("❌ Error al eliminar"); } finally { setLoading(false); }
    };

    // --- ESTA FUNCIÓN SE QUEDA EXACTAMENTE COMO LA TENÍAS (PROMETIDO) ---
    const handleLessonFile = async (file, isVideo = true) => {
        if (!file) return;
        setLoading(true);
        try {
            const folder = isVideo ? 'videos' : 'attachments';
            const cleanName = `${Date.now()}_${file.name.replace(/\s/g, "_")}`;
            const path = `public/courses/lessons/${editingId}/${folder}/${cleanName}`;
            
            const result = await uploadData({ 
                path, 
                data: file,
                options: { 
                    onProgress: (p) => {
                        const progress = Math.round((p.transferredBytes/p.totalBytes)*100);
                        if(isVideo) setUploadProgress(progress);
                        else setAttachmentProgress(progress);
                    }
                }
            }).result;

            const finalKey = result.path.replace('public/', '');
            if (isVideo) setNewLesson(prev => ({ ...prev, videoUrl: finalKey }));
            else setNewLesson(prev => ({ ...prev, content: finalKey }));
            alert("✅ Archivo listo.");
        } catch (err) { alert("❌ Error de S3"); } 
        finally { setLoading(false); setUploadProgress(0); setAttachmentProgress(0); }
    };

    // --- ESTA FUNCIÓN TAMBIÉN SE MANTIENE IGUAL ---
    const handleFileUpload = async (file, type) => {
        if (!file) return;
        setLoading(true);
        try {
            const folder = type === 'thumb' ? 'thumbs' : 'promo';
            const path = `public/courses/${folder}/${Date.now()}_${file.name.replace(/\s/g, "_")}`;
            const result = await uploadData({ path, data: file }).result;
            
            const finalPath = result.path.replace('public/', '');
            if (type === 'thumb') setForm({ ...form, thumbnailUrl: finalPath });
            else setForm({ ...form, promoVideoUrl: finalPath });
            alert("✅ Cargado correctamente.");
        } catch (e) { alert("❌ Error al subir"); }
        finally { setLoading(false); }
    };

    const handleSaveCourse = async () => {
        if (!form.title || !form.priceUSD) return alert("Título y Precio son obligatorios");
        const client = generateClient();
        setLoading(true);
        try {
            const input = { 
                ...form,
                priceUSD: parseFloat(form.priceUSD),
                duration: form.duration || "Indefinida"
            };

            if (editingId) input.id = editingId;
            
            delete input.tempUrl; 
            delete input.lessons; 
            delete input.createdAt; 
            delete input.updatedAt;

            await client.graphql({
                query: editingId ? updateCourse : createCourse,
                variables: { input },
                authMode: 'userPool'
            });

            alert("✅ CURSO GUARDADO EN AWS");
            if (!editingId) setActiveTab('content'); 
            fetchCourses();
            setView('list');
        } catch (e) { console.error(e); alert("Error al guardar."); } 
        finally { setLoading(false); }
    };

    const handleAddLesson = async () => {
        if (!newLesson.title || !newLesson.videoUrl) return alert("⚠️ Falta el video o el título.");
        const client = generateClient();
        setLoading(true);
        try {
            await client.graphql({
                query: createLesson,
                variables: { 
                    input: { ...newLesson, courseID: editingId, order: parseInt(newLesson.order) } 
                },
                authMode: 'userPool'
            });
            alert("✅ ¡LECCIÓN PUBLICADA!");
            setNewLesson({ title: '', videoUrl: '', order: parseInt(newLesson.order) + 1, description: '', content: '' });
            fetchCourses();
        } catch (e) { alert("Error de conexión."); } 
        finally { setLoading(false); }
    };

    // Renderizado (UI) exactamente como lo tenías...
    return (
        <div className="course-manager-container" style={{ padding: '20px', color: '#333', background: '#f4f4f4', minHeight: '100vh' }}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', background: '#fff', padding: '15px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <h2 style={{ margin: 0, color: '#001f3f' }}><FiMonitor /> Academia Scarvaci: Gestión Real</h2>
                <button 
                    onClick={() => { setView(view === 'list' ? 'form' : 'list'); setEditingId(null); setForm(initialForm); }} 
                    style={{ background: '#001f3f', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    {view === 'list' ? <><FiPlus /> Crear Nuevo Curso</> : <><FiArrowLeft /> Volver al Listado</>}
                </button>
            </header>

            {view === 'list' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '25px' }}>
                    {courses.map(c => (
                        <div key={c.id} style={{ background: 'white', borderRadius: '15px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', border: '1px solid #eee' }}>
                            <img 
                                src={c.tempUrl || "https://gioscarvaci.com/documento-legal2.png"} 
                                alt="Course" 
                                style={{ width: '100%', height: '160px', objectFit: 'cover' }} 
                                onError={(e) => { e.target.src = "https://gioscarvaci.com/documento-legal2.png" }}
                            />
                            <div style={{ padding: '20px' }}>
                                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.1rem', color: '#001f3f' }}>{c.title}</h3>
                                <p style={{ color: '#666', fontSize: '0.9rem' }}>Precio: <strong>${c.priceUSD}</strong></p>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                    <button onClick={() => { setForm(c); setEditingId(c.id); setView('form'); setActiveTab('general'); }} style={{ flex: 1, padding: '8px', borderRadius: '5px', border: '1px solid #001f3f', background: 'none', cursor: 'pointer' }}><FiEdit /> Editar</button>
                                    <button onClick={() => { setForm(c); setEditingId(c.id); setView('form'); setActiveTab('content'); }} style={{ flex: 1, padding: '8px', borderRadius: '5px', background: '#001f3f', color: 'white', border: 'none', cursor: 'pointer' }}><FiVideo /> Clases</button>
                                    <button onClick={() => handleDeleteCourse(c.id)} style={{ padding: '8px 12px', borderRadius: '5px', background: '#ff4d4d', color: 'white', border: 'none', cursor: 'pointer' }} title="Eliminar Curso"><FiTrash2 /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                /* ... Resto del componente exactamente igual ... */
                <div style={{ background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 5px 20px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', gap: '40px', borderBottom: '2px solid #eee', marginBottom: '30px' }}>
                        <button onClick={() => setActiveTab('general')} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', borderBottom: activeTab === 'general' ? '3px solid #D4AF37' : 'none', fontWeight: 'bold', color: activeTab === 'general' ? '#D4AF37' : '#999' }}>1. Información General del Curso</button>
                        <button onClick={() => editingId && setActiveTab('content')} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', borderBottom: activeTab === 'content' ? '3px solid #D4AF37' : 'none', fontWeight: 'bold', color: activeTab === 'content' ? '#D4AF37' : '#999', opacity: editingId ? 1 : 0.5 }}>2. Cargar Módulos y Videos</button>
                    </div>

                    {activeTab === 'general' ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div className="form-group">
                                <label style={{fontWeight: 'bold'}}><FiFileText /> Título Profesional</label>
                                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} style={{ width: '100%', padding: '12px', margin: '8px 0', borderRadius: '5px', border: '1px solid #ddd' }} />
                                
                                <label style={{fontWeight: 'bold'}}><FiDollarSign /> Costo USD</label>
                                <input type="number" value={form.priceUSD} onChange={e => setForm({...form, priceUSD: e.target.value})} style={{ width: '100%', padding: '12px', margin: '8px 0', borderRadius: '5px', border: '1px solid #ddd' }} />
                                
                                <label style={{fontWeight: 'bold'}}><FiUsers /> Instructor Responsable</label>
                                <input value={form.instructor} onChange={e => setForm({...form, instructor: e.target.value})} style={{ width: '100%', padding: '12px', margin: '8px 0', borderRadius: '5px', border: '1px solid #ddd' }} />
                                
                                <label style={{fontWeight: 'bold'}}>Dirigido a:</label>
                                <input placeholder="Ej: Especialistas en Propiedad Horizontal" value={form.targetAudience} onChange={e => setForm({...form, targetAudience: e.target.value})} style={{ width: '100%', padding: '12px', margin: '8px 0', borderRadius: '5px', border: '1px solid #ddd' }} />
                            </div>
                            <div className="form-group">
                                <label style={{fontWeight: 'bold'}}><FiImage /> Miniatura del Curso (S3)</label>
                                <input type="file" onChange={(e) => handleFileUpload(e.target.files[0], 'thumb')} style={{ display: 'block', margin: '10px 0' }} />
                                
                                <label style={{fontWeight: 'bold'}}><FiVideo /> Trailer Promocional</label>
                                <input type="file" onChange={(e) => handleFileUpload(e.target.files[0], 'promo')} style={{ display: 'block', margin: '10px 0' }} />

                                <label style={{fontWeight: 'bold'}}>Sinopsis del Curso</label>
                                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} style={{ width: '100%', height: '120px', padding: '12px', marginTop: '8px', borderRadius: '5px', border: '1px solid #ddd' }}></textarea>
                            </div>
                            <button onClick={handleSaveCourse} disabled={loading} style={{ gridColumn: '1 / -1', background: '#D4AF37', color: 'black', padding: '15px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}>
                                {loading ? "GUARDANDO EN LA NUBE..." : "PUBLICAR / ACTUALIZAR CURSO"}
                            </button>
                        </div>
                    ) : (
                        <div style={{ maxWidth: '800px', margin: '0 auto', background: '#fff', padding: '30px', borderRadius: '15px', border: '1px solid #eee' }}>
                            <h3 style={{ textAlign: 'center', marginBottom: '25px', color: '#001f3f' }}><FiVideo /> Cargar Nueva Lección al Módulo</h3>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div>
                                    <label><FiVideo /> 1. Video de la Clase</label>
                                    <input type="file" accept="video/*" onChange={(e) => handleLessonFile(e.target.files[0], true)} style={{ display: 'block', margin: '15px 0', width: '100%' }} />
                                    
                                    {uploadProgress > 0 && (
                                        <div style={{ width: '100%', background: '#eee', borderRadius: '10px', height: '10px', marginBottom: '20px' }}>
                                            <div style={{ width: `${uploadProgress}%`, background: '#D4AF37', height: '100%', borderRadius: '10px' }}></div>
                                        </div>
                                    )}

                                    <label><FiPaperclip /> 4. Material de Apoyo (PDF/Word)</label>
                                    <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => handleLessonFile(e.target.files[0], false)} style={{ display: 'block', margin: '15px 0', width: '100%' }} />
                                    {attachmentProgress > 0 && (
                                        <div style={{ width: '100%', background: '#eee', borderRadius: '10px', height: '10px', marginBottom: '20px' }}>
                                            <div style={{ width: `${attachmentProgress}%`, background: '#3498db', height: '100%', borderRadius: '10px' }}></div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label>2. Título de la Clase</label>
                                    <input value={newLesson.title} onChange={e => setNewLesson({...newLesson, title: e.target.value})} style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: '1px solid #ddd' }} />
                                    
                                    <label>3. Orden</label>
                                    <input type="number" value={newLesson.order} onChange={e => setNewLesson({...newLesson, order: e.target.value})} style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: '1px solid #ddd' }} />
                                </div>
                            </div>

                            <label>Descripción de esta Lección</label>
                            <textarea value={newLesson.description} onChange={e => setNewLesson({...newLesson, description: e.target.value})} style={{ width: '100%', height: '80px', padding: '12px', marginTop: '8px', borderRadius: '5px', border: '1px solid #ddd' }}></textarea>
                            
                            <button onClick={handleAddLesson} disabled={loading || !newLesson.videoUrl} style={{ width: '100%', background: '#001f3f', color: 'white', padding: '15px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', marginTop: '20px' }}>
                                <FiPlus /> VINCULAR LECCIÓN AL CURSO
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CourseManager;